import { UzButton, UzConfirmDialog, UzDialog, UzSwitch } from '@utazzo/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { PositionFields } from '../section-forms';

import { SoftPill } from '@/components/soft-pill';
import { useBranchesList } from '@/features/branches/use-branches';

/**
 * Position tab. A table + modal editor over the SAME OPEN_POSITIONS section
 * items the website renders (single source of truth). Mutations rewrite the
 * items array via `onChange`; the editor's header Save button persists it
 * through the existing queueSection/save pipeline - no dedicated API.
 */

type PositionItem = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strList = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    : [];

/** Read-only preview of a position, mirroring how it reads on the careers page. */
const PositionPreview = ({
  item,
  location,
}: {
  item: PositionItem;
  location: string;
}): React.JSX.Element => {
  const responsibilities = strList(item.responsibilities);
  const skills = strList(item.preferred_skills);
  const meta: Array<[string, string]> = [
    ['Department', str(item.category)],
    ['Location', location],
    ['Work mode', str(item.work_mode)],
    ['Experience', str(item.experience)],
    ['Open roles', typeof item.openings === 'number' ? String(item.openings) : '1'],
  ];
  return (
    <div className="flex flex-col gap-5 text-sm">
      <div>
        {item.is_active !== false ? (
          <SoftPill className="bg-emerald-50 text-emerald-700" dot dotClassName="bg-emerald-500">
            Active
          </SoftPill>
        ) : (
          <SoftPill className="bg-zinc-100 text-zinc-600" dot dotClassName="bg-zinc-400">
            Inactive
          </SoftPill>
        )}
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {meta.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs font-medium text-(--fg-faint)">{label}</dt>
            <dd className="mt-0.5 text-(--fg)">{value || '—'}</dd>
          </div>
        ))}
      </dl>

      {str(item.overview) && (
        <section>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Job overview
          </h4>
          <p className="whitespace-pre-line text-(--fg-muted)">{str(item.overview)}</p>
        </section>
      )}

      {responsibilities.length > 0 && (
        <section>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Roles &amp; responsibilities
          </h4>
          <ul className="list-disc space-y-1 pl-5 text-(--fg-muted)">
            {responsibilities.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </section>
      )}

      {skills.length > 0 && (
        <section>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Preferred skills &amp; qualifications
          </h4>
          <ul className="list-disc space-y-1 pl-5 text-(--fg-muted)">
            {skills.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

const blankPosition = (): PositionItem => ({
  id: crypto.randomUUID(),
  is_active: true,
  title: '',
  category: '',
  branch_id: '',
  location: '',
  work_mode: '',
  experience: '',
  openings: 1,
  overview: '',
  responsibilities: [],
  preferred_skills: [],
});

interface Props {
  positions: PositionItem[];
  canWrite: boolean;
  onChange: (next: PositionItem[]) => void;
}

export const PositionsTab = ({ positions, canWrite, onChange }: Props): React.JSX.Element => {
  const [draft, setDraft] = useState<PositionItem | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PositionItem | null>(null);
  const [preview, setPreview] = useState<PositionItem | null>(null);

  // Resolve a position's hiring branch to its city (falls back to the legacy
  // free-text location), matching what the public careers card shows.
  const branchesQuery = useBranchesList({
    page: 1,
    pageSize: 200,
    isActive: true,
    sortBy: 'sortOrder',
    sortDir: 'asc',
  });
  const locationOf = (p: PositionItem): string => {
    const branch = branchesQuery.data?.data.find((b) => b.id === str(p.branch_id));
    return branch?.city?.name ?? branch?.name ?? str(p.location);
  };

  const openAdd = (): void => {
    setDraft(blankPosition());
    setIsNew(true);
  };
  const openEdit = (p: PositionItem): void => {
    setDraft({ ...p });
    setIsNew(false);
  };

  const patchDraft = (patch: PositionItem): void => setDraft((d) => (d ? { ...d, ...patch } : d));

  const saveDraft = (): void => {
    if (!draft) return;
    const id = str(draft.id) || crypto.randomUUID();
    const next: PositionItem = { ...draft, id };
    onChange(isNew ? [...positions, next] : positions.map((p) => (p.id === id ? next : p)));
    setDraft(null);
  };

  const toggleActive = (p: PositionItem, active: boolean): void =>
    onChange(positions.map((x) => (x.id === p.id ? { ...x, is_active: active } : x)));

  const remove = (p: PositionItem): void => onChange(positions.filter((x) => x.id !== p.id));

  const draftValid = draft ? str(draft.title).trim().length > 0 : false;

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-(--fg-muted)">
          {positions.length} {positions.length === 1 ? 'position' : 'positions'}. Inactive ones are
          hidden on the public site.
        </p>
        {canWrite && (
          <UzButton size="sm" onClick={openAdd}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add New Position
          </UzButton>
        )}
      </div>

      {positions.length === 0 ? (
        <p className="rounded-md border border-dashed border-(--border-strong) p-6 text-center text-sm text-(--fg-faint)">
          No positions yet. Use “Add New Position” to create your first opening.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-(--border)">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-(--border) bg-(--z-50) text-left text-xs text-(--fg-muted)">
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Department</th>
                <th className="px-3 py-2 font-medium">Experience</th>
                <th className="px-3 py-2 font-medium">Work mode</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={str(p.id)} className="border-b border-(--border) last:border-0">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setPreview(p)}
                      className="text-left font-medium text-(--fg) hover:text-(--primary) hover:underline"
                      title="Preview role"
                    >
                      {str(p.title) || <span className="text-(--fg-faint)">Untitled</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-(--fg-muted)">{str(p.category) || '—'}</td>
                  <td className="px-3 py-2 text-(--fg-muted)">{str(p.experience) || '—'}</td>
                  <td className="px-3 py-2 text-(--fg-muted)">{str(p.work_mode) || '—'}</td>
                  <td className="px-3 py-2">
                    <UzSwitch
                      checked={p.is_active !== false}
                      onCheckedChange={(next) => toggleActive(p, next)}
                      disabled={!canWrite}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        className="rounded p-1.5 text-(--fg-faint) hover:bg-(--z-100) hover:text-(--fg)"
                        aria-label="Edit position"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(p)}
                          className="rounded p-1.5 text-(--fg-faint) hover:bg-(--z-100) hover:text-(--ds-error)"
                          aria-label="Delete position"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal (read-only). Opened by clicking the role title. */}
      <UzDialog
        open={preview !== null}
        onOpenChange={(o) => !o && setPreview(null)}
        size="2xl"
        title={str(preview?.title) || 'Position'}
        description="Preview - how this role reads on the careers page."
        footer={
          <>
            <UzButton variant="ghost" onClick={() => setPreview(null)}>
              Close
            </UzButton>
            {canWrite && preview && (
              <UzButton
                onClick={() => {
                  const p = preview;
                  setPreview(null);
                  openEdit(p);
                }}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
              </UzButton>
            )}
          </>
        }
      >
        {preview && <PositionPreview item={preview} location={locationOf(preview)} />}
      </UzDialog>

      {/* Add / edit modal */}
      <UzDialog
        open={draft !== null}
        onOpenChange={(o) => !o && setDraft(null)}
        size="2xl"
        title={isNew ? 'Add new position' : 'Edit position'}
        description="Role details shown on the careers page. Empty roles are skipped."
        footer={
          <>
            <UzButton variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </UzButton>
            <UzButton onClick={saveDraft} disabled={!canWrite || !draftValid}>
              {isNew ? 'Add position' : 'Save'}
            </UzButton>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-3">
            <UzSwitch
              label="Active"
              description="Inactive positions are hidden on the public careers page."
              checked={draft.is_active !== false}
              onCheckedChange={(next) => patchDraft({ is_active: next })}
              disabled={!canWrite}
            />
            <PositionFields item={draft} set={patchDraft} disabled={!canWrite} />
          </div>
        )}
      </UzDialog>

      <UzConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete this position?"
        description={
          confirmDelete
            ? `"${str(confirmDelete.title) || 'Untitled'}" will be removed from the careers page.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={() => {
          if (confirmDelete) remove(confirmDelete);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
};
